import { useEffect, useState } from "react";
import api from "@/components/customAxios/Axios";
import { useToast } from "../toast";
import { IoClose } from "react-icons/io5";

export interface CandidateDetails {
	candidate_name: string;
	title: string;
	email_id: string;
	phone_number: string;
	address: string;
	tech_skills: string[];
	behaviour_skills: string[] | string;
	date_of_birth: string;
	nationality: string;
	linkedin_id: string;
	github_id: string;
	total_experience: string;
}

interface CandidateDetailsPopupProps {
	resume_id: string;
	job_id: number | null;
	setShowDetails: (showDetails: boolean) => void;
	closeDetails: () => void;
}

export default function CandidateDetailsPopup({
	resume_id,
	job_id,
	setShowDetails,
	closeDetails,
}: CandidateDetailsPopupProps) {
	const toast = useToast();
	const [candidateDetails, setCandidateDetails] = useState<CandidateDetails>();

	const showCandDetails = async () => {
		try {
			const res = await api.get(
				`/extracted_info?job_id=${job_id}&resume_id=${resume_id}`
			);
			setCandidateDetails(res.data.extracted_info_details);
			setShowDetails(true);
		} catch (err: any) {
			toast.error({
				duration: 5000,
				status: "error",
				title: "Error Fetching Candidate Details",
				description: err.response?.data?.error || err.response?.data?.message || "Failed to fetch candidate details. Please try again.",
				open: true,
			});
			setShowDetails(false);
		}
	};

	useEffect(() => {
		showCandDetails();
	}, [resume_id, job_id]);

	if (!candidateDetails) {
		return null;
	}

	return (
		<div className="fixed inset-0 bg-gray-800 bg-opacity-20 flex justify-center items-center h-full overflow-auto">
			<div className="relative bg-white dark:bg-zinc-700 rounded-lg shadow p-6 max-w-2xl w-full max-h-[90%] overflow-y-auto">
				<button
					className="absolute top-2 right-2 p-2 rounded-full bg-red-500 text-white hover:bg-red-400 focus:ring-2 focus:ring-red-300"
					onClick={closeDetails}
				>
					<IoClose size={20} />
				</button>
				<div className="bg-white shadow-lg rounded-lg p-6 mb-6 border border-gray-200">
					{/* Candidate Header */}
					<div className="flex flex-wrap items-center justify-between border-b pb-4 mb-4">
						<div>
							<h2 className="text-2xl font-bold text-gray-800">
								{candidateDetails?.candidate_name}
							</h2>
							<p className="text-sm text-gray-500">{candidateDetails.title}</p>
						</div>
						<div className="text-right">
							<p className="text-sm text-gray-700">
								<strong>Email:</strong> {candidateDetails?.email_id}
							</p>
							<p className="text-sm text-gray-700">
								<strong>Phone:</strong> {candidateDetails?.phone_number}
							</p>
							<p className="text-sm text-gray-700">
								<strong>Address:</strong> {candidateDetails?.address}
							</p>
						</div>
					</div>

					{/* Technical Skills */}
					<div className="mb-6">
						<h3 className="text-lg font-semibold text-gray-800 mb-3">
							Technical Skills
						</h3>
						<div className="flex flex-wrap gap-2">
							{candidateDetails?.tech_skills.map((skill, index) => (
								<span
									key={index}
									className="bg-blue-100 text-blue-800 rounded-full px-3 py-1 text-sm font-medium"
								>
									{skill}
								</span>
							))}
						</div>
					</div>

					{/* Behavioral Skills */}
					<div className="mb-6">
						<h3 className="text-lg font-semibold text-gray-800 mb-3">
							Behavioral Skills
						</h3>
						{Array.isArray(candidateDetails?.behaviour_skills) &&
							candidateDetails?.behaviour_skills.length > 0 ? (
							<div className="flex flex-wrap gap-2">
								{candidateDetails?.behaviour_skills.map((skill, index) => (
									<span
										key={index}
										className="bg-green-100 text-green-800 rounded-full px-3 py-1 text-sm font-medium"
									>
										{skill}
									</span>
								))}
							</div>
						) : (
							<p className="text-sm text-gray-500">
								No behavioral skills listed.
							</p>
						)}
					</div>

					{/* Additional Information */}
					<div>
						<h3 className="text-lg font-semibold text-gray-800 mb-3">
							Additional Information
						</h3>
						<ul className="space-y-2">
							<li className="text-sm text-gray-700">
								<strong>Date of Birth:</strong>{" "}
								{candidateDetails?.date_of_birth || "N/A"}
							</li>
							<li className="text-sm text-gray-700">
								<strong>Nationality:</strong>{" "}
								{candidateDetails?.nationality || "N/A"}
							</li>
							<li className="text-sm text-gray-700">
								<strong>LinkedIn:</strong>{" "}
								<a
									href={`https://${candidateDetails?.linkedin_id}`}
									target="_blank"
									rel="noopener noreferrer"
									className="text-blue-500 hover:underline"
								>
									{candidateDetails?.linkedin_id}
								</a>
							</li>
							<li className="text-sm text-gray-700">
								<strong>GitHub:</strong>{" "}
								<a
									href={`https://${candidateDetails?.github_id}`}
									target="_blank"
									rel="noopener noreferrer"
									className="text-blue-500 hover:underline"
								>
									{candidateDetails?.github_id}
								</a>
							</li>
							<li className="text-sm text-gray-700">
								<strong>Total Experience:</strong>{" "}
								{candidateDetails?.total_experience}
							</li>
						</ul>
					</div>
				</div>
			</div>
		</div>
	);
}
